var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
const shouldIgnoreEvent = (target) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  return tagName === "input" || tagName === "textarea" || target.contentEditable === "true";
};
const parseHotkey = (hotkey) => {
  const parts = hotkey.split("+").map((p) => p.trim());
  return {
    ctrl: parts.some((p) => p.toLowerCase() === "ctrl"),
    shift: parts.some((p) => p.toLowerCase() === "shift"),
    alt: parts.some((p) => p.toLowerCase() === "alt"),
    key: parts[parts.length - 1] ?? ""
  };
};
const matchesHotkey = (event, parsed) => {
  return event.ctrlKey === parsed.ctrl && event.shiftKey === parsed.shift && event.altKey === parsed.alt && event.key.toLowerCase() === parsed.key.toLowerCase();
};
class HotkeyManager {
  /**
   * Create a new hotkey manager.
   *
   * @param config - Hotkey configuration.
   * @param onToggleTracing - Callback when toggle tracing hotkey is pressed.
   * @param onToggleDashboard - Callback when toggle dashboard hotkey is pressed.
   */
  constructor(config, onToggleTracing, onToggleDashboard) {
    __publicField(this, "config");
    __publicField(this, "onToggleTracing");
    __publicField(this, "onToggleDashboard");
    __publicField(this, "handler", null);
    this.config = config;
    this.onToggleTracing = onToggleTracing;
    this.onToggleDashboard = onToggleDashboard;
  }
  /**
   * Register hotkeys.
   */
  register() {
    if (this.handler) {
      return;
    }
    const toggleTracingParsed = parseHotkey(this.config.toggleTracing);
    const toggleDashboardParsed = parseHotkey(this.config.toggleDashboard);
    this.handler = (event) => {
      if (shouldIgnoreEvent(event.target)) {
        return;
      }
      if (matchesHotkey(event, toggleTracingParsed)) {
        event.preventDefault();
        this.onToggleTracing();
      } else if (matchesHotkey(event, toggleDashboardParsed)) {
        event.preventDefault();
        this.onToggleDashboard();
      }
    };
    document.addEventListener("keydown", this.handler);
  }
  /**
   * Unregister hotkeys.
   */
  unregister() {
    if (this.handler) {
      document.removeEventListener("keydown", this.handler);
      this.handler = null;
    }
  }
}
const STORAGE_KEY = "autotracer-widget-shown";
const hasBeenShown = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};
const markAsShown = () => {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
  }
};
const DEFAULT_AUTOSTOP_REACT_RENDERS = 200;
const DEFAULT_AUTOSTOP_FLOW_TOPLEVEL = 500;
const DEFAULT_AUTOSTOP_FLOW_TOTAL = 1e4;
const isDarkMode = () => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};
const getPositionClass = (position) => {
  return `dashboard-position-${position}`;
};
const injectStyles = (isDark) => {
  const styleId = "autotracer-dashboard-styles";
  if (document.getElementById(styleId)) {
    return;
  }
  const colors = isDark ? {
    bg: "#1e1e1e",
    bgHover: "#2d2d2d",
    text: "#cccccc",
    border: "#3e3e3e",
    statusRunning: "#25D366",
    statusStopped: "#6e6e6e"
  } : {
    bg: "#ffffff",
    bgHover: "#f3f3f3",
    text: "#333333",
    border: "#d1d1d1",
    statusRunning: "#25D366",
    statusStopped: "#95a5a6"
  };
  const css = `
    .autotracer-dashboard {
      position: fixed;
      z-index: 999999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
    }
    .autotracer-dashboard.dashboard-position-bottom-right { bottom: 20px; right: 20px; }
    .autotracer-dashboard.dashboard-position-bottom-left { bottom: 20px; left: 20px; }
    .autotracer-dashboard.dashboard-position-top-right { top: 20px; right: 20px; }
    .autotracer-dashboard.dashboard-position-top-left { top: 20px; left: 20px; }
    .autotracer-dashboard .dashboard-collapsed {
      width: 35px;
      height: 35px;
      border-radius: 20px;
      background: ${colors.bg};
      border: 2px solid ${colors.border};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      transition: all 0.2s;
    }
    .autotracer-dashboard .dashboard-collapsed:hover {
      background: ${colors.bgHover};
      transform: scale(1.05);
    }
    .autotracer-dashboard .dashboard-status-indicator {
      width: 20px;
      height: 20px;
      border-radius: 10px;
    }
    .autotracer-dashboard .status-running { background: ${colors.statusRunning}; }
    .autotracer-dashboard .status-stopped { background: ${colors.statusStopped}; }
    .autotracer-dashboard .dashboard-expanded {
      min-width: 280px;
      max-height: calc(100vh - 40px);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: ${colors.bg};
      border: 2px solid ${colors.border};
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      color: ${colors.text};
    }
    .autotracer-dashboard .dashboard-header {
      display: flex;
      flex-shrink: 0;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid ${colors.border};
    }
    .autotracer-dashboard .dashboard-title {
      font-weight: 600;
      font-size: 16px;
    }
    .autotracer-dashboard .dashboard-close {
      background: transparent;
      border: none;
      color: ${colors.text};
      cursor: pointer;
      font-size: 20px;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .autotracer-dashboard .dashboard-close:hover {
      background: ${colors.bgHover};
      border-radius: 4px;
    }
    .autotracer-dashboard .dashboard-tracer-section {
      margin-bottom: 12px;
    }
    .autotracer-dashboard .dashboard-tracer-name {
      font-weight: bold;
      font-size: 20px;
      color: ${colors.text};
      opacity: 0.8;
      margin-bottom: 8px;
    }
    .autotracer-dashboard .dashboard-button {
      width: 100%;
      padding: 8px 16px;
      background: ${colors.bgHover};
      border: 1px solid ${colors.border};
      border-radius: 4px;
      color: ${colors.text};
      cursor: pointer;
      font-size: 14px;
      transition: all 0.2s;
    }
    .autotracer-dashboard .dashboard-button:hover {
      background: ${colors.bg};
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-button.running {
      background: ${colors.statusRunning};
      color: ${isDark ? "#1e1e1e" : "#ffffff"};
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-button.running:hover {
      opacity: 0.9;
    }
    .autotracer-dashboard .dashboard-checkbox-label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: ${colors.text};
      opacity: 0.9;
      margin-top: 8px;
      cursor: pointer;
      user-select: none;
    }
    .autotracer-dashboard .dashboard-checkbox-label input[type="checkbox"] {
      cursor: pointer;
      margin: 0;
    }
    .autotracer-dashboard .dashboard-input-group {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: ${colors.text};
      opacity: 0.9;
      margin-top: 8px;
    }
    .autotracer-dashboard .dashboard-input-group label {
      flex-shrink: 0;
    }
    .autotracer-dashboard .dashboard-input-group input[type="number"] {
      width: 70px;
      padding: 2px 6px;
      background: ${colors.bgHover};
      border: 1px solid ${colors.border};
      border-radius: 3px;
      color: ${colors.text};
      font-size: 12px;
    }
    .autotracer-dashboard .dashboard-input-group input[type="number"]:focus {
      outline: none;
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-input-group input[type="number"]:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .autotracer-dashboard .dashboard-validation-error {
      color: #d13438;
      font-size: 11px;
    }
    .autotracer-dashboard .dashboard-input-group input[type="checkbox"] {
      cursor: pointer;
      margin: 0;
    }
    .autotracer-dashboard .dashboard-input-group input[type="text"] {
      flex: 1;
      padding: 4px 8px;
      background: ${colors.bgHover};
      border: 1px solid ${colors.border};
      border-radius: 3px;
      color: ${colors.text};
      font-size: 12px;
    }
    .autotracer-dashboard .dashboard-input-group input[type="text"]:focus {
      outline: none;
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-input-group input[type="text"]::placeholder {
      color: ${colors.text};
      opacity: 0.4;
    }
    .autotracer-dashboard .dashboard-input-group select {
      padding: 4px 8px;
      background: ${colors.bgHover};
      border: 1px solid ${colors.border};
      border-radius: 3px;
      color: ${colors.text};
      font-size: 12px;
      cursor: pointer;
    }
    .autotracer-dashboard .dashboard-input-group select:focus {
      outline: none;
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-advanced {
      width: 100%;
      box-sizing: border-box;
      margin-top: 12px;
      border: 1px solid ${colors.border};
      border-radius: 4px;
      overflow: hidden;
    }
    .autotracer-dashboard .dashboard-advanced-summary {
      padding: 8px 10px;
      background: ${colors.bgHover};
      color: ${colors.text};
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
      user-select: none;
    }
    .autotracer-dashboard .dashboard-advanced-summary:focus-visible {
      outline: 2px solid ${colors.statusRunning};
      outline-offset: -2px;
    }
    .autotracer-dashboard .dashboard-advanced[open] > .dashboard-advanced-summary {
      border-bottom: 1px solid ${colors.border};
    }
    .autotracer-dashboard .dashboard-advanced-content {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 12px;
    }
    .autotracer-dashboard .dashboard-advanced-content > .dashboard-input-group,
    .autotracer-dashboard .dashboard-advanced-content > .dashboard-checkbox-label {
      margin-top: 0;
    }
    .autotracer-dashboard .dashboard-textarea-group {
      display: grid;
      gap: 4px;
      color: ${colors.text};
      font-size: 12px;
      opacity: 0.9;
    }
    .autotracer-dashboard .dashboard-textarea-group textarea {
      width: 100%;
      min-height: 54px;
      box-sizing: border-box;
      padding: 6px 8px;
      resize: vertical;
      background: ${colors.bgHover};
      border: 1px solid ${colors.border};
      border-radius: 3px;
      color: ${colors.text};
      font-family: inherit;
      font-size: 12px;
    }
    .autotracer-dashboard .dashboard-textarea-group textarea:focus {
      outline: none;
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-section-header {
      font-weight: 600;
      font-size: 13px;
      color: ${colors.text};
      opacity: 0.7;
      margin-top: 12px;
      margin-bottom: 4px;
      padding-bottom: 4px;
      border-bottom: 1px solid ${colors.border};
    }
    .autotracer-dashboard .dashboard-btn {
      padding: 6px 12px;
      background: ${colors.bgHover};
      border: 1px solid ${colors.border};
      border-radius: 4px;
      color: ${colors.text};
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
    }
    .autotracer-dashboard .dashboard-btn:hover {
      background: ${colors.bg};
      border-color: ${colors.statusRunning};
    }
    .autotracer-dashboard .dashboard-reset-button {
      align-self: flex-start;
      max-width: 100%;
      box-sizing: border-box;
    }
    .autotracer-dashboard .dashboard-hotkeys {
      font-size: 11px;
      color: ${colors.text};
      opacity: 0.7;
    }
    .autotracer-dashboard .dashboard-scroll-content {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }
    .autotracer-dashboard .dashboard-tab-strip {
      display: flex;
      flex-shrink: 0;
      gap: 2px;
      margin-top: 12px;
      border-top: 1px solid ${colors.border};
    }
    .autotracer-dashboard .dashboard-tab {
      padding: 6px 12px;
      background: transparent;
      border: none;
      border-top: 2px solid transparent;
      color: ${colors.text};
      cursor: pointer;
      font-size: 13px;
      opacity: 0.7;
      margin-top: -1px;
    }
    .autotracer-dashboard .dashboard-tab:hover {
      opacity: 1;
      background: ${colors.bgHover};
    }
    .autotracer-dashboard .dashboard-tab.active {
      opacity: 1;
      border-top-color: ${colors.statusRunning};
      font-weight: 600;
    }
    .autotracer-dashboard .dashboard-hotkeys-title {
      font-weight: 600;
      margin-bottom: 6px;
      opacity: 0.9;
    }
    .autotracer-dashboard .dashboard-hotkey-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }
    .autotracer-dashboard .dashboard-hotkey-label {
      flex: 1;
    }
    .autotracer-dashboard .dashboard-hotkey-keys {
      font-family: 'Courier New', monospace;
      background: ${colors.bgHover};
      padding: 2px 6px;
      border-radius: 3px;
      border: 1px solid ${colors.border};
      font-size: 10px;
    }
    .autotracer-dashboard.dashboard-hidden {
      display: none;
    }
  `;
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = css;
  document.head.appendChild(style);
};
const createCollapsedWidget = (isRunning) => {
  const collapsed = document.createElement("div");
  collapsed.className = "dashboard-collapsed";
  const indicator = document.createElement("div");
  indicator.className = `dashboard-status-indicator ${isRunning ? "status-running" : "status-stopped"}`;
  collapsed.appendChild(indicator);
  return collapsed;
};
const createExpandedWidget = (onClose, onToggleReact, onToggleFlow, onReactEnabledOnLoadChange, onFlowEnabledOnLoadChange, onReactAutoStopChange, onFlowAutoStopTopLevelChange, onFlowAutoStopAllChange, onReactStartTriggerChange, onReactEndTriggerChange, onReactEndTriggerModeChange, onReactTriggerRearmModeChange, onReactClearTriggers, onFlowStartTriggerChange, onFlowEndTriggerChange, onFlowEndTriggerModeChange, onFlowTriggerRearmModeChange, onFlowClearTriggers, reactRunning, flowRunning, reactEnabledOnLoad, flowEnabledOnLoad, reactAutoStopRenders, flowAutoStopTopLevel, flowAutoStopAll, reactStartTrigger, reactEndTrigger, reactEndTriggerMode, reactTriggerRearmMode, flowStartTrigger, flowEndTrigger, flowEndTriggerMode, flowTriggerRearmMode, hasReactTracer, hasFlowTracer, network, hotkeys, activeTab, onTabChange) => {
  function createNetworkCheckbox(label, checked, onChange) {
    const control = document.createElement("label");
    control.className = "dashboard-checkbox-label";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.addEventListener("change", () => onChange(input.checked));
    control.appendChild(input);
    control.appendChild(document.createTextNode(` ${label}`));
    return control;
  }
  function createPositiveIntegerChangeHandler(input, error, onValid) {
    return function handlePositiveIntegerChange() {
      const value = input.valueAsNumber;
      if (!Number.isInteger(value) || value <= 0) {
        error.className = "dashboard-validation-error";
        error.textContent = "Body capture limit must be a positive integer.";
        return;
      }
      error.className = "";
      error.textContent = "";
      onValid(value);
    };
  }
  function createOptionalPositiveIntegerChangeHandler(input, error, onValid) {
    return function handleOptionalPositiveIntegerChange() {
      if (input.value === "") {
        error.className = "";
        error.textContent = "";
        onValid(void 0);
        return;
      }
      const value = input.valueAsNumber;
      if (!Number.isInteger(value) || value <= 0) {
        error.className = "dashboard-validation-error";
        error.textContent = "Automatic stop request limit must be a positive integer.";
        return;
      }
      error.className = "";
      error.textContent = "";
      onValid(value);
    };
  }
  function createGlobListChangeHandler(input, error, onValid) {
    return function handleGlobListChange() {
      const patterns = input.value.split(/\r?\n/u).map((value) => value.trim()).filter((value) => value.length > 0);
      const invalidPattern = patterns.find(
        (pattern) => pattern.includes("{") !== pattern.includes("}")
      );
      if (invalidPattern !== void 0) {
        const label = input.getAttribute("aria-label") ?? "URL globs";
        error.className = "dashboard-validation-error";
        error.textContent = `${label} contain an invalid glob: ${invalidPattern}`;
        return;
      }
      error.className = "";
      error.textContent = "";
      onValid(patterns);
    };
  }
  const expanded = document.createElement("div");
  expanded.className = "dashboard-expanded";
  const header = document.createElement("div");
  header.className = "dashboard-header";
  const title = document.createElement("div");
  title.className = "dashboard-title";
  title.textContent = "AutoTracer";
  const closeBtn = document.createElement("button");
  closeBtn.className = "dashboard-close";
  closeBtn.textContent = "×";
  closeBtn.addEventListener("click", onClose);
  header.appendChild(title);
  header.appendChild(closeBtn);
  expanded.appendChild(header);
  const scrollContent = document.createElement("div");
  scrollContent.className = "dashboard-scroll-content";
  expanded.appendChild(scrollContent);
  const tabStrip = document.createElement("div");
  tabStrip.className = "dashboard-tab-strip";
  const addTab = (id, label) => {
    const tab = document.createElement("button");
    tab.className = `dashboard-tab${activeTab === id ? " active" : ""}`;
    tab.textContent = label;
    tab.addEventListener("click", () => onTabChange(id));
    tabStrip.appendChild(tab);
  };
  if (hasReactTracer) addTab("react", "React");
  if (hasFlowTracer) addTab("flow", "Flow");
  if (network.available) addTab("network", "Network");
  addTab("keys", "Keys");
  if (hasReactTracer) {
    const reactSection = document.createElement("div");
    reactSection.className = "dashboard-tracer-section";
    reactSection.style.display = activeTab === "react" ? "" : "none";
    const reactName = document.createElement("div");
    reactName.className = "dashboard-tracer-name";
    reactName.textContent = "ReactTracer";
    const reactBtn = document.createElement("button");
    reactBtn.className = `dashboard-button ${reactRunning ? "running" : ""}`;
    reactBtn.textContent = reactRunning ? "Stop" : "Start";
    reactBtn.addEventListener("click", onToggleReact);
    const reactCheckbox = document.createElement("label");
    reactCheckbox.className = "dashboard-checkbox-label";
    const reactCheckboxInput = document.createElement("input");
    reactCheckboxInput.type = "checkbox";
    reactCheckboxInput.checked = reactEnabledOnLoad;
    reactCheckboxInput.addEventListener("change", () => {
      onReactEnabledOnLoadChange(reactCheckboxInput.checked);
    });
    reactCheckbox.appendChild(reactCheckboxInput);
    reactCheckbox.appendChild(document.createTextNode(" Enable on load"));
    const reactAutoStopGroup = document.createElement("div");
    reactAutoStopGroup.className = "dashboard-input-group";
    const reactAutoStopCheckbox = document.createElement("input");
    reactAutoStopCheckbox.type = "checkbox";
    reactAutoStopCheckbox.checked = reactAutoStopRenders !== null;
    const reactAutoStopLabel = document.createElement("label");
    reactAutoStopLabel.textContent = "Auto-stop after";
    reactAutoStopLabel.style.cursor = "pointer";
    reactAutoStopLabel.addEventListener("click", () => {
      reactAutoStopCheckbox.click();
    });
    const reactAutoStopInput = document.createElement("input");
    reactAutoStopInput.type = "number";
    reactAutoStopInput.min = "1";
    reactAutoStopInput.value = reactAutoStopRenders !== null ? String(reactAutoStopRenders) : String(DEFAULT_AUTOSTOP_REACT_RENDERS);
    reactAutoStopInput.disabled = reactAutoStopRenders === null;
    reactAutoStopCheckbox.addEventListener("change", () => {
      if (reactAutoStopCheckbox.checked) {
        const value = parseInt(reactAutoStopInput.value, 10) || DEFAULT_AUTOSTOP_REACT_RENDERS;
        reactAutoStopInput.disabled = false;
        onReactAutoStopChange(value);
      } else {
        reactAutoStopInput.disabled = true;
        onReactAutoStopChange(null);
      }
    });
    reactAutoStopInput.addEventListener("change", () => {
      const value = parseInt(reactAutoStopInput.value, 10);
      if (value > 0) {
        onReactAutoStopChange(value);
      }
    });
    const reactAutoStopUnit = document.createTextNode(" renders");
    reactAutoStopGroup.appendChild(reactAutoStopCheckbox);
    reactAutoStopGroup.appendChild(reactAutoStopLabel);
    reactAutoStopGroup.appendChild(reactAutoStopInput);
    reactAutoStopGroup.appendChild(reactAutoStopUnit);
    const reactTriggerHeader = document.createElement("div");
    reactTriggerHeader.className = "dashboard-section-header";
    reactTriggerHeader.textContent = "Triggers (Debug)";
    const reactStartTriggerGroup = document.createElement("div");
    reactStartTriggerGroup.className = "dashboard-input-group";
    const reactStartTriggerLabel = document.createElement("label");
    reactStartTriggerLabel.textContent = "Start when:";
    reactStartTriggerLabel.style.display = "block";
    reactStartTriggerLabel.style.marginBottom = "4px";
    const reactStartTriggerInput = document.createElement("input");
    reactStartTriggerInput.type = "text";
    reactStartTriggerInput.placeholder = "e.g., UserDashboard or User*";
    reactStartTriggerInput.value = reactStartTrigger ?? "";
    reactStartTriggerInput.style.width = "100%";
    reactStartTriggerInput.addEventListener("input", () => {
      const value = reactStartTriggerInput.value.trim();
      onReactStartTriggerChange(value.length > 0 ? value : null);
    });
    reactStartTriggerGroup.appendChild(reactStartTriggerLabel);
    reactStartTriggerGroup.appendChild(reactStartTriggerInput);
    const reactEndTriggerGroup = document.createElement("div");
    reactEndTriggerGroup.className = "dashboard-input-group";
    const reactEndTriggerLabel = document.createElement("label");
    reactEndTriggerLabel.textContent = "Stop when:";
    reactEndTriggerLabel.style.display = "block";
    reactEndTriggerLabel.style.marginBottom = "4px";
    const reactEndTriggerInput = document.createElement("input");
    reactEndTriggerInput.type = "text";
    reactEndTriggerInput.placeholder = "e.g., LogoutPage or *Modal";
    reactEndTriggerInput.value = reactEndTrigger ?? "";
    reactEndTriggerInput.style.width = "100%";
    reactEndTriggerInput.addEventListener("input", () => {
      const value = reactEndTriggerInput.value.trim();
      onReactEndTriggerChange(value.length > 0 ? value : null);
    });
    reactEndTriggerGroup.appendChild(reactEndTriggerLabel);
    reactEndTriggerGroup.appendChild(reactEndTriggerInput);
    const reactTriggerModesGroup = document.createElement("div");
    reactTriggerModesGroup.className = "dashboard-input-group";
    reactTriggerModesGroup.style.display = "flex";
    reactTriggerModesGroup.style.gap = "10px";
    const reactEndModeWrapper = document.createElement("div");
    reactEndModeWrapper.style.flex = "1";
    const reactEndModeLabel = document.createElement("label");
    reactEndModeLabel.textContent = "Stop timing:";
    reactEndModeLabel.style.display = "block";
    reactEndModeLabel.style.marginBottom = "4px";
    const reactEndModeSelect = document.createElement("select");
    reactEndModeSelect.style.width = "100%";
    const reactEndModeEntry = document.createElement("option");
    reactEndModeEntry.value = "on-entry";
    reactEndModeEntry.textContent = "On Entry";
    const reactEndModeExit = document.createElement("option");
    reactEndModeExit.value = "on-exit";
    reactEndModeExit.textContent = "On Exit";
    reactEndModeSelect.appendChild(reactEndModeEntry);
    reactEndModeSelect.appendChild(reactEndModeExit);
    reactEndModeSelect.value = reactEndTriggerMode;
    reactEndModeSelect.addEventListener("change", () => {
      onReactEndTriggerModeChange(
        reactEndModeSelect.value
      );
    });
    reactEndModeWrapper.appendChild(reactEndModeLabel);
    reactEndModeWrapper.appendChild(reactEndModeSelect);
    const reactRearmWrapper = document.createElement("div");
    reactRearmWrapper.style.flex = "1";
    const reactRearmLabel = document.createElement("label");
    reactRearmLabel.textContent = "Auto-start:";
    reactRearmLabel.style.display = "block";
    reactRearmLabel.style.marginBottom = "4px";
    const reactRearmSelect = document.createElement("select");
    reactRearmSelect.style.width = "100%";
    const reactRearmAlways = document.createElement("option");
    reactRearmAlways.value = "always";
    reactRearmAlways.textContent = "Always";
    const reactRearmOnce = document.createElement("option");
    reactRearmOnce.value = "once";
    reactRearmOnce.textContent = "Once";
    reactRearmSelect.appendChild(reactRearmAlways);
    reactRearmSelect.appendChild(reactRearmOnce);
    reactRearmSelect.value = reactTriggerRearmMode;
    reactRearmSelect.addEventListener("change", () => {
      onReactTriggerRearmModeChange(
        reactRearmSelect.value
      );
    });
    reactRearmWrapper.appendChild(reactRearmLabel);
    reactRearmWrapper.appendChild(reactRearmSelect);
    reactTriggerModesGroup.appendChild(reactEndModeWrapper);
    reactTriggerModesGroup.appendChild(reactRearmWrapper);
    const reactClearTriggersButton = document.createElement("button");
    reactClearTriggersButton.className = "dashboard-btn";
    reactClearTriggersButton.textContent = "Clear All Triggers";
    reactClearTriggersButton.style.width = "100%";
    reactClearTriggersButton.style.marginTop = "8px";
    reactClearTriggersButton.addEventListener("click", () => {
      onReactClearTriggers();
      reactStartTriggerInput.value = "";
      reactEndTriggerInput.value = "";
      reactEndModeSelect.value = "on-exit";
      reactRearmSelect.value = "once";
    });
    reactSection.appendChild(reactName);
    reactSection.appendChild(reactBtn);
    reactSection.appendChild(reactCheckbox);
    reactSection.appendChild(reactAutoStopGroup);
    reactSection.appendChild(reactTriggerHeader);
    reactSection.appendChild(reactStartTriggerGroup);
    reactSection.appendChild(reactEndTriggerGroup);
    reactSection.appendChild(reactTriggerModesGroup);
    reactSection.appendChild(reactClearTriggersButton);
    scrollContent.appendChild(reactSection);
  }
  if (hasFlowTracer) {
    const flowSection = document.createElement("div");
    flowSection.className = "dashboard-tracer-section";
    flowSection.style.display = activeTab === "flow" ? "" : "none";
    const flowName = document.createElement("div");
    flowName.className = "dashboard-tracer-name";
    flowName.textContent = "Flow Tracer";
    const flowBtn = document.createElement("button");
    flowBtn.className = `dashboard-button ${flowRunning ? "running" : ""}`;
    flowBtn.textContent = flowRunning ? "Stop" : "Start";
    flowBtn.addEventListener("click", onToggleFlow);
    const flowCheckbox = document.createElement("label");
    flowCheckbox.className = "dashboard-checkbox-label";
    const flowCheckboxInput = document.createElement("input");
    flowCheckboxInput.type = "checkbox";
    flowCheckboxInput.checked = flowEnabledOnLoad;
    flowCheckboxInput.addEventListener("change", () => {
      onFlowEnabledOnLoadChange(flowCheckboxInput.checked);
    });
    flowCheckbox.appendChild(flowCheckboxInput);
    flowCheckbox.appendChild(document.createTextNode(" Enable on load"));
    const flowAutoStopTopLevelGroup = document.createElement("div");
    flowAutoStopTopLevelGroup.className = "dashboard-input-group";
    const flowTopLevelCheckbox = document.createElement("input");
    flowTopLevelCheckbox.type = "checkbox";
    flowTopLevelCheckbox.checked = flowAutoStopTopLevel !== null;
    const flowTopLevelLabel = document.createElement("label");
    flowTopLevelLabel.textContent = "Auto-stop after";
    flowTopLevelLabel.style.cursor = "pointer";
    flowTopLevelLabel.addEventListener("click", () => {
      flowTopLevelCheckbox.click();
    });
    const flowTopLevelInput = document.createElement("input");
    flowTopLevelInput.type = "number";
    flowTopLevelInput.min = "1";
    flowTopLevelInput.value = flowAutoStopTopLevel !== null ? String(flowAutoStopTopLevel) : String(DEFAULT_AUTOSTOP_FLOW_TOPLEVEL);
    flowTopLevelInput.disabled = flowAutoStopTopLevel === null;
    flowTopLevelCheckbox.addEventListener("change", () => {
      if (flowTopLevelCheckbox.checked) {
        const value = parseInt(flowTopLevelInput.value, 10) || DEFAULT_AUTOSTOP_FLOW_TOPLEVEL;
        flowTopLevelInput.disabled = false;
        onFlowAutoStopTopLevelChange(value);
      } else {
        flowTopLevelInput.disabled = true;
        onFlowAutoStopTopLevelChange(null);
      }
    });
    flowTopLevelInput.addEventListener("change", () => {
      const value = parseInt(flowTopLevelInput.value, 10);
      if (value > 0) {
        onFlowAutoStopTopLevelChange(value);
      }
    });
    const flowTopLevelUnit = document.createTextNode(" top-level");
    flowAutoStopTopLevelGroup.appendChild(flowTopLevelCheckbox);
    flowAutoStopTopLevelGroup.appendChild(flowTopLevelLabel);
    flowAutoStopTopLevelGroup.appendChild(flowTopLevelInput);
    flowAutoStopTopLevelGroup.appendChild(flowTopLevelUnit);
    const flowAutoStopAllGroup = document.createElement("div");
    flowAutoStopAllGroup.className = "dashboard-input-group";
    const flowAllCheckbox = document.createElement("input");
    flowAllCheckbox.type = "checkbox";
    flowAllCheckbox.checked = flowAutoStopAll !== null;
    const flowAllLabel = document.createElement("label");
    flowAllLabel.textContent = "Auto-stop after";
    flowAllLabel.style.cursor = "pointer";
    flowAllLabel.addEventListener("click", () => {
      flowAllCheckbox.click();
    });
    const flowAllInput = document.createElement("input");
    flowAllInput.type = "number";
    flowAllInput.min = "1";
    flowAllInput.value = flowAutoStopAll !== null ? String(flowAutoStopAll) : String(DEFAULT_AUTOSTOP_FLOW_TOTAL);
    flowAllInput.disabled = flowAutoStopAll === null;
    flowAllCheckbox.addEventListener("change", () => {
      if (flowAllCheckbox.checked) {
        const value = parseInt(flowAllInput.value, 10) || DEFAULT_AUTOSTOP_FLOW_TOTAL;
        flowAllInput.disabled = false;
        onFlowAutoStopAllChange(value);
      } else {
        flowAllInput.disabled = true;
        onFlowAutoStopAllChange(null);
      }
    });
    flowAllInput.addEventListener("change", () => {
      const value = parseInt(flowAllInput.value, 10);
      if (value > 0) {
        onFlowAutoStopAllChange(value);
      }
    });
    const flowAllUnit = document.createTextNode(" total");
    flowAutoStopAllGroup.appendChild(flowAllCheckbox);
    flowAutoStopAllGroup.appendChild(flowAllLabel);
    flowAutoStopAllGroup.appendChild(flowAllInput);
    flowAutoStopAllGroup.appendChild(flowAllUnit);
    const flowTriggerHeader = document.createElement("div");
    flowTriggerHeader.className = "dashboard-section-header";
    flowTriggerHeader.textContent = "Triggers (Debug)";
    const flowStartTriggerGroup = document.createElement("div");
    flowStartTriggerGroup.className = "dashboard-input-group";
    const flowStartTriggerLabel = document.createElement("label");
    flowStartTriggerLabel.textContent = "Start when:";
    flowStartTriggerLabel.style.display = "block";
    flowStartTriggerLabel.style.marginBottom = "4px";
    const flowStartTriggerInput = document.createElement("input");
    flowStartTriggerInput.type = "text";
    flowStartTriggerInput.placeholder = "e.g., processData or process*";
    flowStartTriggerInput.value = flowStartTrigger ?? "";
    flowStartTriggerInput.style.width = "100%";
    flowStartTriggerInput.addEventListener("input", () => {
      const value = flowStartTriggerInput.value.trim();
      onFlowStartTriggerChange(value.length > 0 ? value : null);
    });
    flowStartTriggerGroup.appendChild(flowStartTriggerLabel);
    flowStartTriggerGroup.appendChild(flowStartTriggerInput);
    const flowEndTriggerGroup = document.createElement("div");
    flowEndTriggerGroup.className = "dashboard-input-group";
    const flowEndTriggerLabel = document.createElement("label");
    flowEndTriggerLabel.textContent = "Stop when:";
    flowEndTriggerLabel.style.display = "block";
    flowEndTriggerLabel.style.marginBottom = "4px";
    const flowEndTriggerInput = document.createElement("input");
    flowEndTriggerInput.type = "text";
    flowEndTriggerInput.placeholder = "e.g., finalize* or complete";
    flowEndTriggerInput.value = flowEndTrigger ?? "";
    flowEndTriggerInput.style.width = "100%";
    flowEndTriggerInput.addEventListener("input", () => {
      const value = flowEndTriggerInput.value.trim();
      onFlowEndTriggerChange(value.length > 0 ? value : null);
    });
    flowEndTriggerGroup.appendChild(flowEndTriggerLabel);
    flowEndTriggerGroup.appendChild(flowEndTriggerInput);
    const flowTriggerModesGroup = document.createElement("div");
    flowTriggerModesGroup.className = "dashboard-input-group";
    flowTriggerModesGroup.style.display = "flex";
    flowTriggerModesGroup.style.gap = "10px";
    const flowEndModeWrapper = document.createElement("div");
    flowEndModeWrapper.style.flex = "1";
    const flowEndModeLabel = document.createElement("label");
    flowEndModeLabel.textContent = "Stop timing:";
    flowEndModeLabel.style.display = "block";
    flowEndModeLabel.style.marginBottom = "4px";
    const flowEndModeSelect = document.createElement("select");
    flowEndModeSelect.style.width = "100%";
    const flowEndModeEntry = document.createElement("option");
    flowEndModeEntry.value = "on-entry";
    flowEndModeEntry.textContent = "On Entry";
    const flowEndModeExit = document.createElement("option");
    flowEndModeExit.value = "on-exit";
    flowEndModeExit.textContent = "On Exit";
    flowEndModeSelect.appendChild(flowEndModeEntry);
    flowEndModeSelect.appendChild(flowEndModeExit);
    flowEndModeSelect.value = flowEndTriggerMode;
    flowEndModeSelect.addEventListener("change", () => {
      onFlowEndTriggerModeChange(
        flowEndModeSelect.value
      );
    });
    flowEndModeWrapper.appendChild(flowEndModeLabel);
    flowEndModeWrapper.appendChild(flowEndModeSelect);
    const flowRearmWrapper = document.createElement("div");
    flowRearmWrapper.style.flex = "1";
    const flowRearmLabel = document.createElement("label");
    flowRearmLabel.textContent = "Auto-start:";
    flowRearmLabel.style.display = "block";
    flowRearmLabel.style.marginBottom = "4px";
    const flowRearmSelect = document.createElement("select");
    flowRearmSelect.style.width = "100%";
    const flowRearmAlways = document.createElement("option");
    flowRearmAlways.value = "always";
    flowRearmAlways.textContent = "Always";
    const flowRearmOnce = document.createElement("option");
    flowRearmOnce.value = "once";
    flowRearmOnce.textContent = "Once";
    flowRearmSelect.appendChild(flowRearmAlways);
    flowRearmSelect.appendChild(flowRearmOnce);
    flowRearmSelect.value = flowTriggerRearmMode;
    flowRearmSelect.addEventListener("change", () => {
      onFlowTriggerRearmModeChange(flowRearmSelect.value);
    });
    flowRearmWrapper.appendChild(flowRearmLabel);
    flowRearmWrapper.appendChild(flowRearmSelect);
    flowTriggerModesGroup.appendChild(flowEndModeWrapper);
    flowTriggerModesGroup.appendChild(flowRearmWrapper);
    const flowClearTriggersButton = document.createElement("button");
    flowClearTriggersButton.className = "dashboard-btn";
    flowClearTriggersButton.textContent = "Clear All Triggers";
    flowClearTriggersButton.style.width = "100%";
    flowClearTriggersButton.style.marginTop = "8px";
    flowClearTriggersButton.addEventListener("click", () => {
      onFlowClearTriggers();
      flowStartTriggerInput.value = "";
      flowEndTriggerInput.value = "";
      flowEndModeSelect.value = "on-exit";
      flowRearmSelect.value = "once";
    });
    flowSection.appendChild(flowName);
    flowSection.appendChild(flowBtn);
    flowSection.appendChild(flowCheckbox);
    flowSection.appendChild(flowAutoStopTopLevelGroup);
    flowSection.appendChild(flowAutoStopAllGroup);
    flowSection.appendChild(flowTriggerHeader);
    flowSection.appendChild(flowStartTriggerGroup);
    flowSection.appendChild(flowEndTriggerGroup);
    flowSection.appendChild(flowTriggerModesGroup);
    flowSection.appendChild(flowClearTriggersButton);
    scrollContent.appendChild(flowSection);
  }
  if (network.available) {
    const networkSection = document.createElement("div");
    networkSection.className = "dashboard-tracer-section";
    networkSection.style.display = activeTab === "network" ? "" : "none";
    const networkName = document.createElement("div");
    networkName.className = "dashboard-tracer-name";
    networkName.textContent = "NetworkTracer";
    const networkButton = document.createElement("button");
    networkButton.className = `dashboard-button ${network.state === "running" ? "running" : ""}`;
    networkButton.textContent = network.state === "running" ? "Stop" : "Start";
    networkButton.addEventListener("click", network.toggle);
    networkSection.appendChild(networkName);
    networkSection.appendChild(networkButton);
    networkSection.appendChild(
      createNetworkCheckbox(
        "Enable on load",
        network.enabledOnLoad,
        network.setEnabledOnLoad
      )
    );
    networkSection.appendChild(
      createNetworkCheckbox(
        "Capture request headers",
        network.captureRequestHeaders,
        network.setCaptureRequestHeaders
      )
    );
    networkSection.appendChild(
      createNetworkCheckbox(
        "Capture request body",
        network.captureRequestBody,
        network.setCaptureRequestBody
      )
    );
    networkSection.appendChild(
      createNetworkCheckbox(
        "Capture response headers",
        network.captureResponseHeaders,
        network.setCaptureResponseHeaders
      )
    );
    networkSection.appendChild(
      createNetworkCheckbox(
        "Capture response body",
        network.captureResponseBody,
        network.setCaptureResponseBody
      )
    );
    const advanced = document.createElement("details");
    advanced.className = "dashboard-advanced";
    const advancedSummary = document.createElement("summary");
    advancedSummary.className = "dashboard-advanced-summary";
    advancedSummary.textContent = "Advanced";
    advanced.appendChild(advancedSummary);
    const advancedContent = document.createElement("div");
    advancedContent.className = "dashboard-advanced-content";
    const bodyLimitGroup = document.createElement("div");
    bodyLimitGroup.className = "dashboard-input-group";
    const bodyLimitLabel = document.createElement("label");
    bodyLimitLabel.textContent = "Body capture limit";
    const bodyLimitInput = document.createElement("input");
    bodyLimitInput.type = "number";
    bodyLimitInput.value = String(network.bodyCaptureLimit);
    const bodyLimitError = document.createElement("span");
    bodyLimitInput.addEventListener(
      "change",
      createPositiveIntegerChangeHandler(
        bodyLimitInput,
        bodyLimitError,
        network.setBodyCaptureLimit
      )
    );
    bodyLimitGroup.appendChild(bodyLimitLabel);
    bodyLimitGroup.appendChild(bodyLimitInput);
    bodyLimitGroup.appendChild(bodyLimitError);
    advancedContent.appendChild(bodyLimitGroup);
    advancedContent.appendChild(
      createNetworkCheckbox(
        "Wait for pending requests on manual stop",
        network.waitForPendingRequestsOnStop,
        network.setWaitForPendingRequestsOnStop
      )
    );
    const autoStopGroup = document.createElement("div");
    autoStopGroup.className = "dashboard-input-group";
    const autoStopLabel = document.createElement("label");
    autoStopLabel.textContent = "Automatic stop request limit";
    const autoStopInput = document.createElement("input");
    autoStopInput.type = "number";
    autoStopInput.value = network.autoStopAfterRequests === void 0 ? "" : String(network.autoStopAfterRequests);
    const autoStopError = document.createElement("span");
    autoStopInput.addEventListener(
      "change",
      createOptionalPositiveIntegerChangeHandler(
        autoStopInput,
        autoStopError,
        network.setAutoStopAfterRequests
      )
    );
    autoStopGroup.appendChild(autoStopLabel);
    autoStopGroup.appendChild(autoStopInput);
    autoStopGroup.appendChild(autoStopError);
    advancedContent.appendChild(autoStopGroup);
    const redactionGroup = document.createElement("div");
    redactionGroup.className = "dashboard-textarea-group";
    const redactionLabel = document.createElement("label");
    redactionLabel.htmlFor = "dashboard-network-redaction-patterns";
    redactionLabel.textContent = "Redaction patterns";
    const redactionInput = document.createElement("textarea");
    redactionInput.id = "dashboard-network-redaction-patterns";
    redactionInput.setAttribute("aria-label", "Redaction patterns");
    redactionInput.value = network.redactionPatterns.join("\n");
    redactionInput.addEventListener(
      "change",
      () => network.setRedactionPatterns(
        redactionInput.value.split(/\r?\n/u).map((value) => value.trim()).filter((value) => value.length > 0)
      )
    );
    redactionGroup.appendChild(redactionLabel);
    redactionGroup.appendChild(redactionInput);
    advancedContent.appendChild(redactionGroup);
    const includeGroup = document.createElement("div");
    includeGroup.className = "dashboard-textarea-group";
    const includeLabel = document.createElement("label");
    includeLabel.htmlFor = "dashboard-network-include-patterns";
    includeLabel.textContent = "Include URL globs";
    const includeInput = document.createElement("textarea");
    includeInput.id = "dashboard-network-include-patterns";
    includeInput.setAttribute("aria-label", "Include URL globs");
    includeInput.value = network.includePatterns.join("\n");
    const includeError = document.createElement("span");
    includeInput.addEventListener(
      "change",
      createGlobListChangeHandler(
        includeInput,
        includeError,
        network.setIncludePatterns
      )
    );
    includeGroup.appendChild(includeLabel);
    includeGroup.appendChild(includeInput);
    includeGroup.appendChild(includeError);
    advancedContent.appendChild(includeGroup);
    const excludeGroup = document.createElement("div");
    excludeGroup.className = "dashboard-textarea-group";
    const excludeLabel = document.createElement("label");
    excludeLabel.htmlFor = "dashboard-network-exclude-patterns";
    excludeLabel.textContent = "Exclude URL globs";
    const excludeInput = document.createElement("textarea");
    excludeInput.id = "dashboard-network-exclude-patterns";
    excludeInput.setAttribute("aria-label", "Exclude URL globs");
    excludeInput.value = network.excludePatterns.join("\n");
    const excludeError = document.createElement("span");
    excludeInput.addEventListener(
      "change",
      createGlobListChangeHandler(
        excludeInput,
        excludeError,
        network.setExcludePatterns
      )
    );
    excludeGroup.appendChild(excludeLabel);
    excludeGroup.appendChild(excludeInput);
    excludeGroup.appendChild(excludeError);
    advancedContent.appendChild(excludeGroup);
    const resetConfigButton = document.createElement("button");
    resetConfigButton.className = "dashboard-btn dashboard-reset-button";
    resetConfigButton.textContent = "Reset configuration";
    resetConfigButton.addEventListener("click", network.resetConfig);
    advancedContent.appendChild(resetConfigButton);
    advanced.appendChild(advancedContent);
    networkSection.appendChild(advanced);
    if (network.state === "stopping") {
      const stoppingStatus = document.createElement("div");
      stoppingStatus.textContent = `Stopping (${network.pendingRequestCount} pending)`;
      const stopNow = document.createElement("button");
      stopNow.className = "dashboard-button";
      stopNow.textContent = "Stop now";
      stopNow.addEventListener("click", network.forceStop);
      networkSection.appendChild(stoppingStatus);
      networkSection.appendChild(stopNow);
    }
    scrollContent.appendChild(networkSection);
  }
  const hotkeysSection = document.createElement("div");
  hotkeysSection.className = "dashboard-hotkeys";
  hotkeysSection.style.display = activeTab === "keys" ? "" : "none";
  const hotkeysTitle = document.createElement("div");
  hotkeysTitle.className = "dashboard-hotkeys-title";
  hotkeysTitle.textContent = "Keyboard Shortcuts";
  hotkeysSection.appendChild(hotkeysTitle);
  const toggleTracingItem = document.createElement("div");
  toggleTracingItem.className = "dashboard-hotkey-item";
  const toggleTracingLabel = document.createElement("span");
  toggleTracingLabel.className = "dashboard-hotkey-label";
  toggleTracingLabel.textContent = "Toggle Tracing";
  const toggleTracingKeys = document.createElement("span");
  toggleTracingKeys.className = "dashboard-hotkey-keys";
  toggleTracingKeys.textContent = hotkeys.toggleTracing;
  toggleTracingItem.appendChild(toggleTracingLabel);
  toggleTracingItem.appendChild(toggleTracingKeys);
  hotkeysSection.appendChild(toggleTracingItem);
  const toggleDashboardItem = document.createElement("div");
  toggleDashboardItem.className = "dashboard-hotkey-item";
  const toggleDashboardLabel = document.createElement("span");
  toggleDashboardLabel.className = "dashboard-hotkey-label";
  toggleDashboardLabel.textContent = "Toggle Dashboard";
  const toggleDashboardKeys = document.createElement("span");
  toggleDashboardKeys.className = "dashboard-hotkey-keys";
  toggleDashboardKeys.textContent = hotkeys.toggleDashboard;
  toggleDashboardItem.appendChild(toggleDashboardLabel);
  toggleDashboardItem.appendChild(toggleDashboardKeys);
  hotkeysSection.appendChild(toggleDashboardItem);
  scrollContent.appendChild(hotkeysSection);
  expanded.appendChild(tabStrip);
  return expanded;
};
class DashboardWidget {
  /**
   * Create a new dashboard widget.
   *
   * @param config - Normalized dashboard configuration.
   */
  constructor(config) {
    __publicField(this, "config");
    __publicField(this, "container");
    __publicField(this, "isExpanded", false);
    __publicField(this, "isVisible", false);
    __publicField(this, "activeTab", null);
    __publicField(this, "clickAwayListener", null);
    __publicField(this, "statePollingInterval", null);
    __publicField(this, "lastKnownState", {
      hasReactTracer: false,
      hasFlowTracer: false,
      hasNetworkTracer: false,
      networkState: "stopped",
      networkPendingRequestCount: 0,
      reactRunning: false,
      flowRunning: false
    });
    this.config = config;
    this.container = document.createElement("div");
    this.container.className = `autotracer-dashboard ${getPositionClass(config.position)}`;
    injectStyles(isDarkMode());
    this.initializeVisibility();
    this.render();
    this.startStatePolling();
  }
  /**
   * Start polling for tracer state changes.
   */
  startStatePolling() {
    this.lastKnownState = {
      hasReactTracer: this.hasReactTracer(),
      hasFlowTracer: this.hasFlowTracer(),
      hasNetworkTracer: this.hasNetworkTracer(),
      networkState: this.getNetworkTracerState(),
      networkPendingRequestCount: this.getNetworkPendingRequestCount(),
      reactRunning: this.isReactTracerRunning(),
      flowRunning: this.isFlowTracerRunning()
    };
    this.statePollingInterval = window.setInterval(() => {
      const currentState = {
        hasReactTracer: this.hasReactTracer(),
        hasFlowTracer: this.hasFlowTracer(),
        hasNetworkTracer: this.hasNetworkTracer(),
        networkState: this.getNetworkTracerState(),
        networkPendingRequestCount: this.getNetworkPendingRequestCount(),
        reactRunning: this.isReactTracerRunning(),
        flowRunning: this.isFlowTracerRunning()
      };
      if (currentState.hasReactTracer !== this.lastKnownState.hasReactTracer || currentState.hasFlowTracer !== this.lastKnownState.hasFlowTracer || currentState.hasNetworkTracer !== this.lastKnownState.hasNetworkTracer || currentState.networkState !== this.lastKnownState.networkState || currentState.networkPendingRequestCount !== this.lastKnownState.networkPendingRequestCount || currentState.reactRunning !== this.lastKnownState.reactRunning || currentState.flowRunning !== this.lastKnownState.flowRunning) {
        this.clearUnavailableActiveTab(
          currentState.hasReactTracer,
          currentState.hasFlowTracer,
          currentState.hasNetworkTracer
        );
        this.lastKnownState = currentState;
        this.render();
      }
    }, 500);
  }
  /**
   * Stop polling for tracer state changes.
   */
  stopStatePolling() {
    if (this.statePollingInterval !== null) {
      clearInterval(this.statePollingInterval);
      this.statePollingInterval = null;
    }
  }
  /**
   * Initialize widget visibility based on configuration and localStorage.
   */
  initializeVisibility() {
    if (hasBeenShown()) {
      this.isVisible = true;
    } else if (!this.config.hideByDefault) {
      this.isVisible = true;
    } else {
      this.isVisible = false;
    }
    if (!this.isVisible) {
      this.container.classList.add("dashboard-hidden");
    }
  }
  /**
   * Render widget DOM.
   */
  render() {
    this.container.innerHTML = "";
    if (this.isExpanded) {
      const expanded = createExpandedWidget(
        () => this.collapse(),
        () => this.toggleReactTracer(),
        () => this.toggleFlowTracer(),
        (enabled) => this.setReactEnabledOnLoad(enabled),
        (enabled) => this.setFlowEnabledOnLoad(enabled),
        (limit) => this.setReactAutoStopRenders(limit),
        (limit) => this.setFlowAutoStopTopLevel(limit),
        (limit) => this.setFlowAutoStopAll(limit),
        (pattern) => this.setReactStartTrigger(pattern),
        (pattern) => this.setReactEndTrigger(pattern),
        (mode) => this.setReactEndTriggerMode(mode),
        (mode) => this.setReactTriggerRearmMode(mode),
        () => this.clearReactTriggers(),
        (pattern) => this.setFlowStartTrigger(pattern),
        (pattern) => this.setFlowEndTrigger(pattern),
        (mode) => this.setFlowEndTriggerMode(mode),
        (mode) => this.setFlowTriggerRearmMode(mode),
        () => this.clearFlowTriggers(),
        this.isReactTracerRunning(),
        this.isFlowTracerRunning(),
        this.getReactEnabledOnLoad(),
        this.getFlowEnabledOnLoad(),
        this.getReactAutoStopRenders(),
        this.getFlowAutoStopTopLevel(),
        this.getFlowAutoStopAll(),
        this.getReactStartTrigger(),
        this.getReactEndTrigger(),
        this.getReactEndTriggerMode(),
        this.getReactTriggerRearmMode(),
        this.getFlowStartTrigger(),
        this.getFlowEndTrigger(),
        this.getFlowEndTriggerMode(),
        this.getFlowTriggerRearmMode(),
        this.hasReactTracer(),
        this.hasFlowTracer(),
        {
          available: this.hasNetworkTracer(),
          state: this.getNetworkTracerState(),
          pendingRequestCount: this.getNetworkPendingRequestCount(),
          enabledOnLoad: this.getNetworkEnabledOnLoad(),
          captureRequestHeaders: this.getNetworkCaptureRequestHeaders(),
          captureRequestBody: this.getNetworkCaptureRequestBody(),
          captureResponseHeaders: this.getNetworkCaptureResponseHeaders(),
          captureResponseBody: this.getNetworkCaptureResponseBody(),
          bodyCaptureLimit: this.getNetworkBodyCaptureLimit(),
          waitForPendingRequestsOnStop: this.getNetworkWaitForPendingRequestsOnStop(),
          autoStopAfterRequests: this.getNetworkAutoStopAfterRequests(),
          redactionPatterns: this.getNetworkRedactionPatterns(),
          includePatterns: this.getNetworkIncludePatterns(),
          excludePatterns: this.getNetworkExcludePatterns(),
          toggle: () => this.toggleNetworkTracer(),
          forceStop: () => this.forceStopNetworkTracer(),
          setEnabledOnLoad: (value) => this.setNetworkEnabledOnLoad(value),
          setCaptureRequestHeaders: (value) => this.setNetworkCaptureRequestHeaders(value),
          setCaptureRequestBody: (value) => this.setNetworkCaptureRequestBody(value),
          setCaptureResponseHeaders: (value) => this.setNetworkCaptureResponseHeaders(value),
          setCaptureResponseBody: (value) => this.setNetworkCaptureResponseBody(value),
          setBodyCaptureLimit: (value) => this.setNetworkBodyCaptureLimit(value),
          setWaitForPendingRequestsOnStop: (value) => this.setNetworkWaitForPendingRequestsOnStop(value),
          setAutoStopAfterRequests: (value) => this.setNetworkAutoStopAfterRequests(value),
          setRedactionPatterns: (value) => this.setNetworkRedactionPatterns(value),
          setIncludePatterns: (value) => this.setNetworkIncludePatterns(value),
          setExcludePatterns: (value) => this.setNetworkExcludePatterns(value),
          resetConfig: () => this.resetNetworkConfig()
        },
        this.config.hotkeys,
        this.resolveActiveTab(),
        (tab) => this.setActiveTab(tab)
      );
      this.container.appendChild(expanded);
    } else {
      const collapsed = createCollapsedWidget(this.isAnyTracerRunning());
      collapsed.addEventListener("click", () => this.expand());
      this.container.appendChild(collapsed);
    }
  }
  /**
   * Check if React tracer is available.
   */
  hasReactTracer() {
    var _a;
    return Boolean((_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer);
  }
  /**
   * Check if Flow tracer is available.
   */
  hasFlowTracer() {
    var _a;
    return Boolean((_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer);
  }
  /**
   * Check if Network tracer is available.
   */
  hasNetworkTracer() {
    var _a;
    return Boolean((_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer);
  }
  /** Returns the current NetworkTracer lifecycle state. */
  getNetworkTracerState() {
    var _a, _b;
    return ((_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getState()) ?? "stopped";
  }
  /** Returns the current NetworkTracer pending-work count. */
  getNetworkPendingRequestCount() {
    var _a, _b;
    return ((_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getPendingRequestCount()) ?? 0;
  }
  /**
   * Clears an explicit tab selection when its tracer is no longer available.
   *
   * @param hasReactTracer - Whether ReactTracer is currently available.
   * @param hasFlowTracer - Whether FlowTracer is currently available.
   * @param hasNetworkTracer - Whether NetworkTracer is currently available.
   */
  clearUnavailableActiveTab(hasReactTracer, hasFlowTracer, hasNetworkTracer) {
    if (this.activeTab === "react" && !hasReactTracer) {
      this.activeTab = null;
    }
    if (this.activeTab === "flow" && !hasFlowTracer) {
      this.activeTab = null;
    }
    if (this.activeTab === "network" && !hasNetworkTracer) {
      this.activeTab = null;
    }
  }
  /**
   * Resolve active tab, defaulting to the first available tracer tab.
   *
   * @returns Active tab identifier.
   */
  resolveActiveTab() {
    if (this.activeTab === "keys") return "keys";
    if (this.activeTab === "react" && this.hasReactTracer()) return "react";
    if (this.activeTab === "flow" && this.hasFlowTracer()) return "flow";
    if (this.activeTab === "network" && this.hasNetworkTracer()) {
      return "network";
    }
    if (this.hasReactTracer()) return "react";
    if (this.hasFlowTracer()) return "flow";
    if (this.hasNetworkTracer()) return "network";
    return "keys";
  }
  /**
   * Set active tab and re-render.
   *
   * @param tab - Tab to activate.
   */
  setActiveTab(tab) {
    this.activeTab = tab;
    this.render();
  }
  /**
   * Check if React tracer is running.
   */
  isReactTracerRunning() {
    var _a, _b;
    return Boolean((_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer) == null ? void 0 : _b.isEnabled());
  }
  /**
   * Check if Flow tracer is running.
   */
  isFlowTracerRunning() {
    var _a, _b;
    return Boolean((_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer) == null ? void 0 : _b.isEnabled());
  }
  /**
   * Check if any tracer is currently running.
   * Used for collapsed widget icon color.
   */
  isAnyTracerRunning() {
    return this.isReactTracerRunning() || this.isFlowTracerRunning();
  }
  /**
   * Toggle React tracer state.
   */
  toggleReactTracer() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    if (reactTracer.isEnabled()) {
      reactTracer.stop();
    } else {
      reactTracer.start();
      this.markAsShownAndShow();
    }
    this.render();
  }
  /**
   * Toggle Flow tracer state.
   */
  toggleFlowTracer() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    if (flowTracer.isEnabled()) {
      flowTracer.stop();
    } else {
      flowTracer.start();
      this.markAsShownAndShow();
    }
    this.render();
  }
  /** Toggles NetworkTracer between running and stopped states. */
  toggleNetworkTracer() {
    var _a;
    const networkTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer;
    if (networkTracer === void 0) return;
    if (networkTracer.getState() === "running") {
      networkTracer.stop();
    } else {
      networkTracer.start();
      this.markAsShownAndShow();
    }
    this.render();
  }
  /** Immediately stops a draining NetworkTracer session. */
  forceStopNetworkTracer() {
    var _a, _b;
    (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.forceStop();
    this.render();
  }
  /** Returns NetworkTracer enabled-on-load state. */
  getNetworkEnabledOnLoad() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getEnabledOnLoad) == null ? void 0 : _c.call(_b)) ?? false;
  }
  /** Persists NetworkTracer enabled-on-load state. */
  setNetworkEnabledOnLoad(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setEnabledOnLoad) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns request-header capture state. */
  getNetworkCaptureRequestHeaders() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getCaptureRequestHeaders) == null ? void 0 : _c.call(_b)) ?? false;
  }
  /** Persists request-header capture state. */
  setNetworkCaptureRequestHeaders(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setCaptureRequestHeaders) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns request-body capture state. */
  getNetworkCaptureRequestBody() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getCaptureRequestBody) == null ? void 0 : _c.call(_b)) ?? false;
  }
  /** Persists request-body capture state. */
  setNetworkCaptureRequestBody(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setCaptureRequestBody) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns response-header capture state. */
  getNetworkCaptureResponseHeaders() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getCaptureResponseHeaders) == null ? void 0 : _c.call(_b)) ?? false;
  }
  /** Persists response-header capture state. */
  setNetworkCaptureResponseHeaders(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setCaptureResponseHeaders) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns response-body capture state. */
  getNetworkCaptureResponseBody() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getCaptureResponseBody) == null ? void 0 : _c.call(_b)) ?? false;
  }
  /** Persists response-body capture state. */
  setNetworkCaptureResponseBody(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setCaptureResponseBody) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns the NetworkTracer body capture limit. */
  getNetworkBodyCaptureLimit() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getBodyCaptureLimit) == null ? void 0 : _c.call(_b)) ?? 65536;
  }
  /** Persists the NetworkTracer body capture limit. */
  setNetworkBodyCaptureLimit(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setBodyCaptureLimit) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns the NetworkTracer manual-stop drain policy. */
  getNetworkWaitForPendingRequestsOnStop() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getWaitForPendingRequestsOnStop) == null ? void 0 : _c.call(_b)) ?? false;
  }
  /** Persists the NetworkTracer manual-stop drain policy. */
  setNetworkWaitForPendingRequestsOnStop(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setWaitForPendingRequestsOnStop) == null ? void 0 : _c.call(
      _b,
      value
    );
  }
  /** Returns the NetworkTracer automatic-stop request limit. */
  getNetworkAutoStopAfterRequests() {
    var _a, _b, _c;
    return (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getAutoStopAfterRequests) == null ? void 0 : _c.call(_b);
  }
  /** Persists the NetworkTracer automatic-stop request limit. */
  setNetworkAutoStopAfterRequests(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setAutoStopAfterRequests) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns detached NetworkTracer redaction patterns. */
  getNetworkRedactionPatterns() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getRedactionPatterns) == null ? void 0 : _c.call(_b)) ?? [];
  }
  /** Persists NetworkTracer redaction patterns. */
  setNetworkRedactionPatterns(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setRedactionPatterns) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns detached NetworkTracer inclusion patterns. */
  getNetworkIncludePatterns() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getIncludePatterns) == null ? void 0 : _c.call(_b)) ?? [];
  }
  /** Persists NetworkTracer inclusion patterns. */
  setNetworkIncludePatterns(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setIncludePatterns) == null ? void 0 : _c.call(_b, value);
  }
  /** Returns detached NetworkTracer exclusion patterns. */
  getNetworkExcludePatterns() {
    var _a, _b, _c;
    return ((_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.getExcludePatterns) == null ? void 0 : _c.call(_b)) ?? [];
  }
  /** Persists NetworkTracer exclusion patterns. */
  setNetworkExcludePatterns(value) {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.setExcludePatterns) == null ? void 0 : _c.call(_b, value);
  }
  /** Restores NetworkTracer configuration defaults. */
  resetNetworkConfig() {
    var _a, _b, _c;
    (_c = (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.networkTracer) == null ? void 0 : _b.resetConfig) == null ? void 0 : _c.call(_b);
    this.render();
  }
  /**
   * Mark widget as shown and ensure it's visible.
   */
  markAsShownAndShow() {
    markAsShown();
    if (!this.isVisible) {
      this.show();
    }
  }
  /**
   * Expand widget to show controls.
   */
  expand() {
    this.isExpanded = true;
    this.render();
    this.clickAwayListener = (event) => {
      if (!this.container.contains(event.target)) {
        this.collapse();
      }
    };
    setTimeout(() => {
      document.addEventListener("click", this.clickAwayListener, true);
    }, 0);
  }
  /**
   * Collapse widget to pill state.
   */
  collapse() {
    this.isExpanded = false;
    this.render();
    if (this.clickAwayListener) {
      document.removeEventListener("click", this.clickAwayListener, true);
      this.clickAwayListener = null;
    }
  }
  /**
   * Show the widget.
   */
  show() {
    this.isVisible = true;
    this.container.classList.remove("dashboard-hidden");
  }
  /**
   * Hide the widget.
   */
  hide() {
    this.isVisible = false;
    this.container.classList.add("dashboard-hidden");
  }
  /**
   * Toggle widget visibility.
   */
  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }
  /**
   * Check if widget is visible.
   */
  isWidgetVisible() {
    return this.isVisible;
  }
  /**
   * Mount widget to DOM.
   *
   * @param rootElement - Container element to mount into.
   */
  mount(rootElement) {
    rootElement.appendChild(this.container);
  }
  /**
   * Unmount widget from DOM.
   */
  unmount() {
    if (this.clickAwayListener) {
      document.removeEventListener("click", this.clickAwayListener, true);
      this.clickAwayListener = null;
    }
    this.stopStatePolling();
    this.container.remove();
  }
  /**
   * Get React tracer enabledOnLoad state.
   * Falls back to direct localStorage read if tracer not initialized yet.
   */
  getReactEnabledOnLoad() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (reactTracer == null ? void 0 : reactTracer.getEnabledOnLoad) {
      return reactTracer.getEnabledOnLoad();
    }
    try {
      const value = localStorage.getItem("autotracer-react-enabled-on-load");
      return value === "true";
    } catch {
      return false;
    }
  }
  /**
   * Set React tracer enabledOnLoad state.
   */
  setReactEnabledOnLoad(enabled) {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.setEnabledOnLoad) == null ? void 0 : _b.call(reactTracer, enabled);
  }
  /**
   * Get Flow tracer enabledOnLoad state.
   * Falls back to direct localStorage read if tracer not initialized yet.
   */
  getFlowEnabledOnLoad() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getEnabledOnLoad) {
      return flowTracer.getEnabledOnLoad();
    }
    try {
      const value = localStorage.getItem("autotracer-flow-enabled-on-load");
      return value === "true";
    } catch {
      return false;
    }
  }
  /**
   * Set Flow tracer enabledOnLoad state.
   */
  setFlowEnabledOnLoad(enabled) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setEnabledOnLoad) == null ? void 0 : _b.call(flowTracer, enabled);
  }
  /**
   * Get React tracer auto-stop limit.
   */
  getReactAutoStopRenders() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (reactTracer == null ? void 0 : reactTracer.getAutoStopAfterRenders) {
      return reactTracer.getAutoStopAfterRenders();
    }
    try {
      const value = localStorage.getItem("autotracer-react-autostop-renders");
      if (value === null) return null;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) || parsed <= 0 ? null : parsed;
    } catch {
      return null;
    }
  }
  /**
   * Set React tracer auto-stop limit.
   */
  setReactAutoStopRenders(limit) {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.setAutoStopAfterRenders) == null ? void 0 : _b.call(reactTracer, limit);
  }
  /**
   * Get Flow tracer auto-stop top-level limit.
   */
  getFlowAutoStopTopLevel() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getAutoStopTopLevel) {
      return flowTracer.getAutoStopTopLevel();
    }
    try {
      const value = localStorage.getItem("autotracer-flow-autostop-toplevel");
      if (value === null) return null;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) || parsed <= 0 ? null : parsed;
    } catch {
      return null;
    }
  }
  /**
   * Set Flow tracer auto-stop top-level limit.
   */
  setFlowAutoStopTopLevel(limit) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setAutoStopTopLevel) == null ? void 0 : _b.call(flowTracer, limit);
  }
  /**
   * Get Flow tracer auto-stop all functions limit.
   */
  getFlowAutoStopAll() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getAutoStopAll) {
      return flowTracer.getAutoStopAll();
    }
    try {
      const value = localStorage.getItem("autotracer-flow-autostop-all");
      if (value === null) return null;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) || parsed <= 0 ? null : parsed;
    } catch {
      return null;
    }
  }
  /**
   * Set Flow tracer auto-stop all functions limit.
   */
  setFlowAutoStopAll(limit) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setAutoStopAll) == null ? void 0 : _b.call(flowTracer, limit);
  }
  // React Tracer Trigger Methods
  /**
   * Get React tracer start trigger pattern.
   */
  getReactStartTrigger() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (reactTracer == null ? void 0 : reactTracer.getStartTrigger) {
      return reactTracer.getStartTrigger();
    }
    return null;
  }
  /**
   * Set React tracer start trigger pattern.
   */
  setReactStartTrigger(pattern) {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.setStartTrigger) == null ? void 0 : _b.call(reactTracer, pattern);
  }
  /**
   * Get React tracer end trigger pattern.
   */
  getReactEndTrigger() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (reactTracer == null ? void 0 : reactTracer.getEndTrigger) {
      return reactTracer.getEndTrigger();
    }
    return null;
  }
  /**
   * Set React tracer end trigger pattern.
   */
  setReactEndTrigger(pattern) {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.setEndTrigger) == null ? void 0 : _b.call(reactTracer, pattern);
  }
  /**
   * Get React tracer end trigger mode.
   */
  getReactEndTriggerMode() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (reactTracer == null ? void 0 : reactTracer.getEndTriggerMode) {
      return reactTracer.getEndTriggerMode();
    }
    return "on-exit";
  }
  /**
   * Set React tracer end trigger mode.
   */
  setReactEndTriggerMode(mode) {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.setEndTriggerMode) == null ? void 0 : _b.call(reactTracer, mode);
  }
  /**
   * Get React tracer trigger rearm mode.
   */
  getReactTriggerRearmMode() {
    var _a;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (reactTracer == null ? void 0 : reactTracer.getTriggerRearmMode) {
      return reactTracer.getTriggerRearmMode();
    }
    return "once";
  }
  /**
   * Set React tracer trigger rearm mode.
   */
  setReactTriggerRearmMode(mode) {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.setTriggerRearmMode) == null ? void 0 : _b.call(reactTracer, mode);
  }
  /**
   * Clear all React tracer triggers.
   */
  clearReactTriggers() {
    var _a, _b;
    const reactTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer;
    if (!reactTracer) {
      return;
    }
    (_b = reactTracer.clearAllTriggers) == null ? void 0 : _b.call(reactTracer);
  }
  // Flow Tracer Trigger Methods
  /**
   * Get Flow tracer start trigger pattern.
   */
  getFlowStartTrigger() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getStartTrigger) {
      return flowTracer.getStartTrigger();
    }
    return null;
  }
  /**
   * Set Flow tracer start trigger pattern.
   */
  setFlowStartTrigger(pattern) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setStartTrigger) == null ? void 0 : _b.call(flowTracer, pattern);
  }
  /**
   * Get Flow tracer end trigger pattern.
   */
  getFlowEndTrigger() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getEndTrigger) {
      return flowTracer.getEndTrigger();
    }
    return null;
  }
  /**
   * Set Flow tracer end trigger pattern.
   */
  setFlowEndTrigger(pattern) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setEndTrigger) == null ? void 0 : _b.call(flowTracer, pattern);
  }
  /**
   * Get Flow tracer end trigger mode.
   */
  getFlowEndTriggerMode() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getEndTriggerMode) {
      return flowTracer.getEndTriggerMode();
    }
    return "on-exit";
  }
  /**
   * Set Flow tracer end trigger mode.
   */
  setFlowEndTriggerMode(mode) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setEndTriggerMode) == null ? void 0 : _b.call(flowTracer, mode);
  }
  /**
   * Get Flow tracer trigger rearm mode.
   */
  getFlowTriggerRearmMode() {
    var _a;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (flowTracer == null ? void 0 : flowTracer.getTriggerRearmMode) {
      return flowTracer.getTriggerRearmMode();
    }
    return "once";
  }
  /**
   * Set Flow tracer trigger rearm mode.
   */
  setFlowTriggerRearmMode(mode) {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.setTriggerRearmMode) == null ? void 0 : _b.call(flowTracer, mode);
  }
  /**
   * Clear all Flow tracer triggers.
   */
  clearFlowTriggers() {
    var _a, _b;
    const flowTracer = (_a = globalThis.autoTracer) == null ? void 0 : _a.flowTracer;
    if (!flowTracer) {
      return;
    }
    (_b = flowTracer.clearAllTriggers) == null ? void 0 : _b.call(flowTracer);
  }
}
const normalizeConfig = (config) => {
  var _a, _b;
  return {
    enabled: true,
    hideByDefault: config.hideByDefault ?? true,
    position: config.position ?? "bottom-right",
    hotkeys: {
      toggleTracing: ((_a = config.hotkeys) == null ? void 0 : _a.toggleTracing) ?? "Alt+Shift+T",
      toggleDashboard: ((_b = config.hotkeys) == null ? void 0 : _b.toggleDashboard) ?? "Alt+Shift+D"
    }
  };
};
const toggleAllTracers = () => {
  var _a, _b, _c;
  let anyStarted = false;
  if ((_a = globalThis.autoTracer) == null ? void 0 : _a.reactTracer) {
    if (globalThis.autoTracer.reactTracer.isEnabled()) {
      globalThis.autoTracer.reactTracer.stop();
    } else {
      globalThis.autoTracer.reactTracer.start();
      anyStarted = true;
    }
  }
  if ((_b = globalThis.autoTracer) == null ? void 0 : _b.flowTracer) {
    if (globalThis.autoTracer.flowTracer.isEnabled()) {
      globalThis.autoTracer.flowTracer.stop();
    } else {
      globalThis.autoTracer.flowTracer.start();
      anyStarted = true;
    }
  }
  if (anyStarted) {
    markAsShown();
    if ((_c = globalThis.autoTracer) == null ? void 0 : _c.widget) {
      globalThis.autoTracer.widget.show();
    }
  }
};
const mountDashboard = (config = {}) => {
  var _a;
  const rootId = "autotracer-dashboard-root";
  const existingRoot = document.getElementById(rootId);
  const hasMountedDashboard = existingRoot !== null && existingRoot.querySelector(".autotracer-dashboard") !== null;
  if ((_a = globalThis.autoTracer) == null ? void 0 : _a.widget) {
    return;
  }
  if (hasMountedDashboard && globalThis.__autoTracerDashboardControls) {
    globalThis.autoTracer = globalThis.autoTracer ?? {};
    globalThis.autoTracer.widget = globalThis.__autoTracerDashboardControls;
    return;
  }
  if (hasMountedDashboard) {
    return;
  }
  const injectedConfig = globalThis.__autoTracerDashboardConfig ?? {};
  const mergedConfig = { ...injectedConfig, ...config };
  if (mergedConfig.enabled === false) {
    return;
  }
  const normalized = normalizeConfig(mergedConfig);
  let root = existingRoot;
  if (!root) {
    root = document.createElement("div");
    root.id = rootId;
    document.body.appendChild(root);
  }
  const widget = new DashboardWidget(normalized);
  widget.mount(root);
  const hotkeyManager = new HotkeyManager(
    normalized.hotkeys,
    toggleAllTracers,
    () => widget.toggle()
  );
  hotkeyManager.register();
  if (!globalThis.autoTracer) {
    globalThis.autoTracer = {};
  }
  const controls = {
    show: () => widget.show(),
    hide: () => widget.hide(),
    toggle: () => widget.toggle(),
    isVisible: () => widget.isWidgetVisible(),
    unregisterHotkeys: () => hotkeyManager.unregister()
  };
  globalThis.__autoTracerDashboardControls = controls;
  globalThis.autoTracer.widget = controls;
};
const toggleDashboard = () => {
  var _a, _b;
  mountDashboard();
  (_b = (_a = globalThis.autoTracer) == null ? void 0 : _a.widget) == null ? void 0 : _b.toggle();
};
export {
  mountDashboard,
  toggleDashboard
};
